// Thin client for the inference backend.
//
// ``/inference`` takes the file alone and answers with the results plus the name the
// annotated image was stored under. The image itself is fetched from ``/image/{name}``,
// and only exists when the backend has STORE_IMAGES on: with it off nothing is drawn or
// written, and the name comes back null.

export type InferenceResult = {
  results: unknown;
  imageName: string | null;
};

// A stored name is one flat segment, so it is encoded whole — a name carrying a path
// separator stays escaped and the backend answers 400 rather than serving another file.
export const imageUrl = (imageName: string) => `/image/${encodeURIComponent(imageName)}`;

export class InferenceError extends Error {
  readonly status: number | null;

  constructor(message: string, status: number | null) {
    super(message);
    this.name = 'InferenceError';
    this.status = status;
  }
}

export async function runInference(file: File): Promise<InferenceResult> {
  const formData = new FormData();
  formData.append('file', file);

  const response = await fetch('/inference', { method: 'POST', body: formData });
  if (!response.ok) {
    const text = await response.text().catch(() => '');
    throw new InferenceError(
      text || `Request failed with status ${response.status}`,
      response.status,
    );
  }

  const data = (await response.json()) as { results?: unknown; image_name?: string | null };
  return { results: data.results ?? null, imageName: data.image_name || null };
}
